import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { GoogleAddressInput } from "../components/GoogleAddressInput";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { PartyPopper, Calculator, Save, FileDown, Trash2, Loader2, Building2, Search, Send } from "lucide-react";
import { eur, STATI, StatoBadge } from "./PreventiviPage";

const RISORSE = [["ambulanza", "Ambulanza"], ["pattuglia", "Pattuglia"], ["medico", "Medico"], ["infermiere", "Infermiere"], ["soccorritore", "Soccorritore"]];
const EMPTY = {
  cliente: { nominativo: "", indirizzo: "", email: "", telefono: "", origine: "" },
  data: new Date().toISOString().slice(0, 10), stato: "bozza",
  denominazione: "", luogo: { indirizzo: "", lat: null, lng: null }, inizio: "", fine: "", ore: null,
  unita: Object.fromEntries(RISORSE.map(([k]) => [k, 0])), totale_forzato: null, note: "", salva_cliente: false,
};

const Sez = ({ icon: Icon, title, children }) => (
  <Card className="border-slate-200 p-5">
    <div className="flex items-center gap-2 mb-4"><Icon className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-slate-800">{title}</h2></div>
    <div className="grid md:grid-cols-2 gap-4">{children}</div>
  </Card>
);
const F = ({ label, children, full }) => <div className={full ? "md:col-span-2" : ""}><Label className="nx-label">{label}</Label>{children}</div>;

const RichiedenteSearch = ({ value, onChange, onPick }) => {
  const [all, setAll] = useState([]); const [show, setShow] = useState(false); const t = useRef(null);
  useEffect(() => { api.get("/richiedenti").then(({ data }) => setAll(data)).catch(() => {}); }, []);
  const res = value.trim().length >= 1 ? all.filter((r) => (r.nome || "").toLowerCase().includes(value.toLowerCase())).slice(0, 10) : [];
  return (
    <div className="relative md:col-span-2">
      <Label className="nx-label">Richiedente * (autocompletamento da anagrafica Richiedenti)</Label>
      <div className="relative"><Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 mt-0.75" />
        <Input data-testid="pev-richiedente" value={value} onChange={(e) => { onChange(e.target.value); setShow(true); }} onFocus={() => setShow(true)}
          onBlur={() => { t.current = setTimeout(() => setShow(false), 150); }} className="mt-1.5 pl-8" placeholder="Digita il nome del richiedente..." /></div>
      {show && res.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {res.map((r) => (
            <button type="button" key={r.id || r.nome} data-testid="pev-richiedente-opt" onMouseDown={(e) => { e.preventDefault(); onPick(r); setShow(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
              <span className="font-medium text-slate-800">{r.nome}</span>{r.indirizzo && <span className="text-slate-400"> · {r.indirizzo}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PreventivoEventoFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "nuovo";
  const [p, setP] = useState(EMPTY);
  const [codice, setCodice] = useState(null);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [oreManuali, setOreManuali] = useState(false);
  const [forzaTotale, setForzaTotale] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailMsg, setMailMsg] = useState("");
  const [sending, setSending] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const setC = (k, v) => setP((prev) => ({ ...prev, cliente: { ...prev.cliente, [k]: v } }));

  useEffect(() => {
    if (isNew) return;
    api.get(`/amministrazione/preventivi-eventi/${id}`).then(({ data }) => {
      setP({ ...EMPTY, ...data, unita: { ...EMPTY.unita, ...(data.unita || {}) }, salva_cliente: false });
      setCodice(data.codice); setCalc(data.calcolo);
      setOreManuali(data.ore != null); setForzaTotale(!!data.calcolo?.forzato);
    }).catch(() => navigate("/operativa/amministrazione/preventivi?tipo=evento"));
  }, [id, isNew, navigate]);

  const normalize = useCallback((x) => ({
    ...x, ore: oreManuali && x.ore !== "" && x.ore != null ? Number(x.ore) : null,
    unita: Object.fromEntries(Object.entries(x.unita).map(([k, v]) => [k, Number(v) || 0])),
    totale_forzato: forzaTotale && x.totale_forzato !== "" && x.totale_forzato != null ? Number(x.totale_forzato) : null,
  }), [oreManuali, forzaTotale]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/amministrazione/preventivi-eventi/calcola", normalize(p)); setCalc(data); }
      catch (err) { console.warn("Calcolo preventivo evento non riuscito", err); toast.error("Calcolo non riuscito", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    }, 300);
    return () => clearTimeout(t);
  }, [p, normalize]);

  const save = async () => {
    if (!p.cliente.nominativo.trim()) return toast.error("Inserisci il richiedente");
    setSaving(true);
    try {
      const body = normalize(p);
      const { data } = isNew ? await api.post("/amministrazione/preventivi-eventi", body) : await api.put(`/amministrazione/preventivi-eventi/${id}`, body);
      toast.success(isNew ? `Preventivo ${data.codice} creato` : "Preventivo aggiornato");
      if (isNew) navigate(`/operativa/amministrazione/preventivi-eventi/${data.id}`, { replace: true }); else setCalc(data.calcolo);
    } catch (err) { toast.error("Non salvato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Eliminare il preventivo ${codice}?`)) return;
    try { await api.delete(`/amministrazione/preventivi-eventi/${id}`); toast.success("Preventivo eliminato"); navigate("/operativa/amministrazione/preventivi?tipo=evento"); }
    catch { toast.error("Non eliminato"); }
  };
  const setStato = async (stato) => {
    try { const { data } = await api.patch(`/amministrazione/preventivi-eventi/${id}/stato`, { stato }); set("stato", data.stato); toast.success(`Stato: ${STATI[stato][0]}`); }
    catch { toast.error("Stato non aggiornato"); }
  };
  const downloadPdf = async () => {
    const res = await api.get(`/amministrazione/preventivi-eventi/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${codice}.pdf`; a.click(); URL.revokeObjectURL(url);
  };
  const sendMail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/amministrazione/preventivi-eventi/${id}/invia`, { messaggio: mailMsg });
      set("stato", "inviato"); setMailOpen(false); toast.success(`Preventivo inviato a ${data.to}`);
    } catch (err) { toast.error("Invio non riuscito", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSending(false); }
  };

  return (
    <AppLayout crumbLabel={codice || "Nuovo preventivo evento"}>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 data-testid="pev-title" className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><PartyPopper className="h-7 w-7 text-primary" /> {codice || "Nuovo preventivo evento"}</h1>
            <p className="text-sm text-slate-500 flex items-center gap-2">{isNew ? "Numerazione PREV-EV separata, assegnata al salvataggio." : <>Creato il {p.created_at?.slice(0, 10).split("-").reverse().join("/")} da {p.created_by || "—"} <StatoBadge stato={p.stato} /></>}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isNew && (
              <Select value={p.stato || "bozza"} onValueChange={setStato}>
                <SelectTrigger data-testid="pev-stato" className="h-9 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATI).map(([v, [l]]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {!isNew && <Button data-testid="pev-converti" variant="gold" className="gap-2" onClick={() => navigate(`/operativa/nuovo-evento?preventivo=${id}`)}><PartyPopper className="h-4 w-4" /> Converti in evento</Button>}
            {!isNew && <Button data-testid="pev-invia" variant="outline" className="gap-2" onClick={() => { setMailMsg(`con la presente Vi trasmettiamo il preventivo ${codice} per il servizio di assistenza sanitaria all'evento "${p.denominazione}". Restiamo a disposizione per ogni chiarimento.`); setMailOpen(true); }}><Send className="h-4 w-4" /> Invia email</Button>}
            {!isNew && <Button data-testid="pev-pdf" variant="outline" className="gap-2" onClick={downloadPdf}><FileDown className="h-4 w-4" /> PDF</Button>}
            {!isNew && <Button data-testid="pev-delete" variant="destructive" className="gap-2" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}
            <Button data-testid="pev-save" className="gap-2" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Crea preventivo" : "Salva"}</Button>
          </div>
        </div>

        <Sez icon={Building2} title="Dati generali">
          <RichiedenteSearch value={p.cliente.nominativo} onChange={(v) => setC("nominativo", v)}
            onPick={(r) => setP((prev) => ({ ...prev, cliente: { nominativo: r.nome || "", indirizzo: r.indirizzo || "", email: r.email || "", telefono: r.telefono || "", origine: "Richiedenti" } }))} />
          <F label="Mail"><Input data-testid="pev-cliente-email" type="email" value={p.cliente.email} onChange={(e) => setC("email", e.target.value)} className="mt-1.5" /></F>
          <F label="Telefono"><Input data-testid="pev-cliente-telefono" value={p.cliente.telefono} onChange={(e) => setC("telefono", e.target.value)} className="mt-1.5" /></F>
          <F label="Denominazione Evento *"><Input data-testid="pev-denominazione" value={p.denominazione} onChange={(e) => set("denominazione", e.target.value)} className="mt-1.5" placeholder="es. Sagra di paese, Concerto…" /></F>
          <F label="Data preventivo"><Input data-testid="pev-data" type="date" value={p.data} onChange={(e) => set("data", e.target.value)} className="mt-1.5" /></F>
          <F label="Luogo dell'Evento" full>
            <GoogleAddressInput testid="pev-luogo" value={p.luogo.indirizzo} onChange={(v) => set("luogo", { indirizzo: v, lat: null, lng: null })} onPick={(val) => set("luogo", val)} />
          </F>
          <F label="Data e Ora Inizio"><Input data-testid="pev-inizio" type="datetime-local" value={p.inizio} onChange={(e) => set("inizio", e.target.value)} className="mt-1.5" /></F>
          <F label="Data e Ora Fine"><Input data-testid="pev-fine" type="datetime-local" value={p.fine} onChange={(e) => set("fine", e.target.value)} className="mt-1.5" /></F>
          <label className="md:col-span-2 flex items-center gap-2 cursor-pointer select-none">
            <Checkbox data-testid="pev-salva-cliente" checked={p.salva_cliente} onCheckedChange={(v) => set("salva_cliente", !!v)} />
            <span className="text-sm text-slate-600">Salva questo richiedente in anagrafica Preventivi</span>
          </label>
        </Sez>

        <Sez icon={Calculator} title="Calcolo preventivo — unità × tariffa oraria × ore">
          <div className="md:col-span-2 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {RISORSE.map(([k, l]) => (
              <F key={k} label={`${l} (unità)`}>
                <Input data-testid={`pev-unita-${k}`} type="number" min="0" step="1" value={p.unita[k] ?? 0} onChange={(e) => set("unita", { ...p.unita, [k]: e.target.value })} className="mt-1.5" />
              </F>
            ))}
          </div>
          <F label={`Ore totali servizio ${oreManuali ? "(manuali)" : "(auto da inizio/fine)"}`}>
            <Input data-testid="pev-ore" type="number" min="0" step="0.25" value={oreManuali ? (p.ore ?? "") : (calc?.ore_auto ?? 0)} readOnly={!oreManuali}
              onChange={(e) => set("ore", e.target.value)} className={`mt-1.5 ${oreManuali ? "" : "bg-slate-50"}`} />
          </F>
          <label className="flex items-center gap-2 cursor-pointer select-none self-end pb-2.5">
            <Checkbox data-testid="pev-ore-manuali" checked={oreManuali} onCheckedChange={(v) => { setOreManuali(!!v); if (v) set("ore", calc?.ore_auto ?? 0); else set("ore", null); }} />
            <span className="text-sm text-slate-700">Modifica manualmente le ore</span>
          </label>
          <label className="md:col-span-2 flex items-center gap-2 cursor-pointer select-none">
            <Checkbox data-testid="pev-forza-totale" checked={forzaTotale} onCheckedChange={(v) => { setForzaTotale(!!v); if (v) set("totale_forzato", calc?.totale_calcolato ?? 0); else set("totale_forzato", null); }} />
            <span className="text-sm text-slate-700">Modifica manualmente il Totale Preventivo</span>
          </label>
          {forzaTotale && <F label="Totale Preventivo (€)"><Input data-testid="pev-totale-forzato" type="number" step="0.01" min="0" value={p.totale_forzato ?? ""} onChange={(e) => set("totale_forzato", e.target.value)} className="mt-1.5" /></F>}
          <F label="Note" full><Textarea data-testid="pev-note" value={p.note} onChange={(e) => set("note", e.target.value)} className="mt-1.5" rows={3} /></F>
        </Sez>

        <Card className="border-primary/30 bg-primary/5 p-5" data-testid="pev-calcolo">
          <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /> Dettaglio calcolo (uso interno, non nel PDF)</h2>
          {!calc ? <p className="text-sm text-slate-500">Calcolo in corso…</p> : (
            <table className="w-full text-sm">
              <tbody>
                {calc.voci.length === 0 && <tr><td className="py-2 text-slate-500">Inserisci almeno una unità per calcolare il totale.</td></tr>}
                {calc.voci.map((v) => (
                  <tr key={v.voce} className="border-b border-slate-200/70">
                    <td className="py-2 text-slate-700">{v.voce}</td>
                    <td className="py-2 text-right text-slate-500">{v.unita} × {eur(v.tariffa_ora)}/h × {v.ore}h</td>
                    <td className="py-2 text-right font-semibold text-slate-800 w-28">{eur(v.totale)}</td>
                  </tr>
                ))}
                {calc.forzato && <tr><td colSpan={2} className="pt-2 text-sm text-slate-500">Totale calcolato</td><td data-testid="pev-totale-calcolato" className="pt-2 text-right text-sm text-slate-500 line-through">{eur(calc.totale_calcolato)}</td></tr>}
                <tr><td colSpan={2} className="pt-3 text-base font-bold text-slate-900">TOTALE PREVENTIVO {calc.forzato && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">manuale</span>}</td><td data-testid="pev-totale" className="pt-3 text-right text-xl font-bold text-primary">{eur(calc.totale)}</td></tr>
              </tbody>
            </table>
          )}
        </Card>

        <Dialog open={mailOpen} onOpenChange={setMailOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invia preventivo evento via email</DialogTitle>
              <DialogDescription>Destinatario: <b>{p.cliente.email || "— nessuna email nel richiedente —"}</b>. Il PDF {codice} sarà allegato con i link per accettare o rifiutare.</DialogDescription></DialogHeader>
            <Textarea data-testid="pev-mail-msg" rows={5} value={mailMsg} onChange={(e) => setMailMsg(e.target.value)} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setMailOpen(false)}>Annulla</Button>
              <Button data-testid="pev-mail-send" onClick={sendMail} disabled={sending || !p.cliente.email}>{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
